import { useIsMutating } from "@tanstack/react-query";
import {
  AddUser1Icon,
  ArrowLeftIcon,
  ArrowRightIcon,
  Box,
  CheckFilledIcon,
  CheckListIcon,
  CloseIcon,
  DocumentIcon,
  GKButton,
  Grid2,
  IconButton,
  KycIcon,
  Modal,
  RupeeIcon,
  SearchIcon,
  SendIcon,
  Show,
  Tab,
  Tabs,
  TagBorderIcon,
  Typography,
  UserIcon,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { AdvisorOnboardFormInterface } from "interfaces/OnboardingInterface";
import { RiskProfileListInterface } from "interfaces/RiskProfileInterface";
import { split, sum } from "lodash";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import { GET_RISK_PROFILE } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { UserRoleTypes } from "utils/common";
import { GKModalProps } from "utils/commonInterface";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import AdvisorOnboardingAgreement from "./AdvisorOnboardingAgreement";
import AdvisorOnboardingClientDetails from "./AdvisorOnboardingClientDetails";
import AdvisorOnboardingCompleted from "./AdvisorOnboardingCompleted";
import AdvisorOnboardingInvestments from "./AdvisorOnboardingInvestments";
import AdvisorOnboardingPayment from "./AdvisorOnboardingPayment";
import AdvisorOnboardingRiskProfile from "./AdvisorOnboardingRiskProfile";
import AdvisorOnboardingTypeSelect from "./AdvisorOnboardingTypeSelect";
import SendClientModal from "./SendClientModal";

const AdvisorOnboarding = ({ open, setOpen }: GKModalProps) => {
  const userData = useAppSelector((state) => state?.user?.userData);
  const onBoardflowData = useAppSelector(
    (state) => state?.app?.onBoardflowData,
  );

  const [hasError, setHasError] = useState(false);

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const form = useForm<AdvisorOnboardFormInterface>({
    defaultValues: {
      activeStep: 0,
      skipAdvanceFee: false,
      type: "advisor",
      clientId: null,
      mfEntity: "",
      personalDetails: {
        email: "",
        name: "",
        gender: "",
        maritalStatus: "",
        fatherName: "",
        motherName: "",
        dateOfBirth: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        income: "",
        occupation: "",
        pan: "",
        aadhar: "",
        aadhaar_number: "",
        emergencyContact: "",
        emergencyContactNum: "",
        country_of_birth: "",
        place_of_birth: "",
        nationality_country: "",
        source_of_wealth: "",
      },
      branch: "",
      family: [],
      signatureType: onBoardflowData?.defaultAdvisorSignType || "",
      isExistingClient: "falseValue",
      phone: "",
      countryCode: "+91",
      bankAccounts: [
        { name: "", accountNum: "", type: "", ifsc: "", confirmAccountNum: "" },
      ],
      dematAccounts: [
        {
          brokerName: "",
          dematId: "",
          uniqClientCode: "",
          depositary: "",
        },
      ],
      nominees: [],
      kyc: null,
      document: [],
      id: "",
      sendOptions: "",
      manual: "",
      identification_type: onBoardflowData?.kycIdentificationType || "",
      riskProfile: null,
      agreement: "",
      basketObject: null,
      partner: null,
      portfolioObject: null,
      riskProfileObject: null,
      selectedFeePlan: null,
      planDataObject: {
        aumCash: "",
        showHoldingsModal: false,
        aumBasedPlan: "",
        subscriptionPlan: null,
        holdingsValue: null,
        subscriptionPlanAccountType: null,
        holdingsProofs: [],
        cashProofs: [],
        plansHoldings: [
          {
            gridCode: "",
            quantity: 0,
            cmp: "",
            id: 0,
          },
        ],
      },
      answers: [],
    },
  });

  form.watch();

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const { data = { data: [] } } = useFetchQuery<any>({
    key: [QueryKeys.MODEL_PORTFOLIO_LIST],
    route: "/model_portfolio/summary/",
  });
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  useFetchQuery({
    key: ["RISK_PROFILE_LIST"],
    route: GET_RISK_PROFILE,
    enabled: [UserRoleTypes.advisor, UserRoleTypes.partner].includes(
      userData?.userRole,
    ),
    success: (data: RiskProfileListInterface) => {
      form.setValue("riskProfileObject", data?.data?.data?.data);
    },
  });
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  useFetchQuery({
    key: [QueryKeys.GET_CLIENT_BY_ID],
    route: `/client/${form.getValues("id")}/`,
    enabled: Boolean(form.getValues("id")),
    success: (data: any) => {
      form.reset({
        ...form.getValues(),
        ...data?.data,
        activeStep: form.getValues("activeStep"),
        selectedFeePlan: data?.data?.basketIds[0]?.feePlan || null,
        basketObject: data?.data?.basketIds[0]?.basket || null,
        portfolioObject: data?.data?.basketIds[0]?.portfolio || null,
        mfInvestmentMode: data?.data?.dematAccounts?.length ? "demat" : "folio",
        type: form.getValues("type"),
        bankAccounts:
          data?.data?.bankAccounts?.length === 0
            ? [
                {
                  name: "",
                  accountNum: "",
                  type: "",
                  ifsc: "",
                  confirmAccountNum: "",
                },
              ]
            : data?.data?.bankAccounts,
        dematAccounts:
          data?.data?.dematAccounts?.length === 0
            ? [
                {
                  brokerName: "",
                  dematId: "",
                  uniqClientCode: "",
                  depositary: "",
                },
              ]
            : data?.data?.bankAccounts,
      });
    },
  });
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  useFetchQuery({
    key: [QueryKeys.GET_COMPLIANCE_BY_CLIENT_ID],
    route: `/compliance/?client_id=${form.getValues("id")}`,
    enabled: Boolean(form.getValues("id")),
    success: (response: any) => {
      form.setValue("kyc", response?.data?.data?.kyc);
      form.setValue("riskProfile", response?.data?.data?.riskProfile);
      form.setValue("agreement", response?.data?.data?.agreement);
    },
  });
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===

  const riskProfileMatch =
    +form.getValues("riskProfile.points") >
    +split(form.getValues("basketObject.riskProfilePoints") || "0 - 0", " ")[0];
  const onboardingType = form.getValues("onboardingType") || [];
  const isOnlyMfOnboarding =
    onboardingType.includes("MF") && !onboardingType.includes("RIA");

  const fpOnboardingSession = (form.getValues() as any)?.fpOnboardingSession;
  const isMFKYCComplete =
    fpOnboardingSession?.fpPreVerificationStatus === "completed" &&
    fpOnboardingSession?.fpReadinessStatus === "verified";

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const { mutate: sendRiskProfile, isLoading: riskLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(`/riskprofile-request/`, { client: data }),
    mutationKey: ["RISK_PROFILE_CLIENT_REQUEST"],

    onSuccess: () => {
      form.setValue("activeStep", form.getValues("activeStep") + 1);
    },
  });

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const TabsData = [
    {
      icon: UserIcon,
      label: "Onboarding",
      show: true,
      isCompleted: form.getValues("activeStep") !== 0,
      disabled: form.getValues("activeStep") !== 0,
      component: <AdvisorOnboardingTypeSelect basketList={data?.data} />,
      value: "CHOOSE",
    },
    {
      icon: KycIcon,
      label: "Personal Info.",
      show: true,
      isCompleted: form.getValues("id"),
      disabled: form.getValues("id"),
      component: (
        <AdvisorOnboardingClientDetails
          setHasError={setHasError}
          hasError={hasError}
          setOpen={setOpen}
        />
      ),
      value: "DETAILS",
    },
    // {
    //   icon: BulletedListIcon,
    //   label: "Basket",
    //   show:
    //     form.getValues("type") === "manual"
    //       ? investorOnBoardflowData?.customizeSetting?.basketFeePlan
    //       : onBoardflowData?.customizeSetting?.basketFeePlan,
    //   isCompleted: form.getValues("basketObject.id"),
    //   disabled: false,
    //   component: (
    //     <AdvisorOnboardingEquityBasket basketList={data} setOpen={setOpen} />
    //   ),
    //   value: "BASKET",
    // },
    {
      icon: TagBorderIcon,
      label: "Plans",
      show:
        !isOnlyMfOnboarding &&
        onBoardflowData?.customizeSetting?.investmentDetails,
      isCompleted: false,
      disabled: false,
      component: <AdvisorOnboardingInvestments />,
      value: "INVESTMENTS",
    },
    // {
    //   icon: CardsThreeIcon,
    //   label: "KYC",
    //   show:
    //
    //     onBoardflowData?.customizeSetting?.kyc,
    //   isCompleted: form.getValues("kyc.kycId"),
    //   disabled: false,
    //   component: <AdvisorOnboardingKYC />,
    //   value: "KYC",
    // },
    {
      icon: CheckListIcon,
      label: "Risk Profile",
      show:
        !isOnlyMfOnboarding && onBoardflowData?.customizeSetting?.riskprofiling,
      isCompleted: form.getValues("riskProfile.points"),
      disabled: false,
      component: <AdvisorOnboardingRiskProfile />,
      value: "RISK_ASSESSMENT",
    },
    {
      icon: DocumentIcon,
      label: "Agreement",
      show: !isOnlyMfOnboarding && onBoardflowData?.customizeSetting?.agreement,
      isCompleted: form.getValues("agreement.agreementStatus.id"),
      disabled: false,
      component: <AdvisorOnboardingAgreement />,
      value: "AGREEMENT",
    },
    {
      icon: RupeeIcon,
      label: "Payment",
      show: !isOnlyMfOnboarding && onBoardflowData?.customizeSetting?.payment,
      isCompleted: form.getValues("activeStep") > 7,
      disabled: false,
      component: <AdvisorOnboardingPayment />,
      value: "PAYMENT",
    },
    {
      icon: SearchIcon,
      label: "Review You Details",
      show: form.getValues("type") === "advisor" || isOnlyMfOnboarding,
      isCompleted: false,
      disabled: false,
      component: <AdvisorOnboardingCompleted />,
      value: "COMPLETED",
    },
  ];

  // ================================================================================================================= clientData={addedClientData}====
  const { mutate: handleSendPaymentLink, isLoading: sendLinkLoading } =
    useMutation({
      mutationKey: ["HANDLE_SEND_PAYMENT_LINK"],
      mutationFn: (values: any) =>
        axiosInstance.post(`/api/revenue/onboarding-flow/`, values),
      onSuccess: () => {
        refetchQuery(QueryKeys.GET_COMPLIANCE_BY_CLIENT_ID);
        refetchQuery(QueryKeys.GET_CLIENT_BY_ID);
        form.setValue("activeStep", form.getValues("activeStep") + 1);
      },
    });
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const handleCurrentForm = (currentStep: string) => {
    switch (currentStep) {
      case "CHOOSE":
        return {
          buttonText: "Next",
          form: "advisor-onboarding-onboarding-form",
        };
      case "DETAILS":
        return {
          buttonText: "Next",
          form: "advisor-onboarding-client-details-form",
        };
      // case "BASKET":
      //   return {
      //     buttonText:
      //       form.getValues("type") !== "manual" ? "Next" : "Send & Finish",
      //     form: "advisor-onboarding-equity-basket-form",
      //   };
      case "INVESTMENTS":
        return {
          buttonText: "Next",
          form: "aum-fee-plan-form",
        };
      case "MF_INVESTMENTS":
        return {
          buttonText: "Next",
          form: "mf-investment-form",
        };
      // case "KYC":
      //   return {
      //     buttonText: "Next",
      //     form: "advisor-onboarding-kyc-form",
      //   };
      case "RISK_ASSESSMENT":
        return {
          buttonText: "Next",
          form: "advisor-onboarding-risk-profile-form",
        };
      case "AGREEMENT":
        return {
          buttonText: "Next",
          form: "advisor-onboarding-agreement-form",
        };
      case "PAYMENT":
        return {
          buttonText: "Next",
          form: "advisor-onboarding-payment-form",
        };
      case "COMPLETED":
        return {
          buttonText: "Finish",
          form: "completed-form",
        };

      default:
        return {
          buttonText: "Next",
          form: "",
        };
    }
  };

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const agreementLoading = !!useIsMutating({
    mutationKey: ["HANDLE_ADD_AGREMENT_MUTATUION"],
  });
  const addClientLoading = !!useIsMutating({
    mutationKey: ["HANDLE_ADD_CLIENT_MUTATUION"],
  });
  const updateClientLoading = !!useIsMutating({
    mutationKey: ["HANDLE_UPDATE_CLIENT_MUTATUION"],
  });
  const riskProfileLoading = !!useIsMutating({
    mutationKey: ["HANDLE_RISK_PROFILE_MUTATUION"],
  });
  const equityBasketLoading = !!useIsMutating({
    mutationKey: ["HANDLE_CLIENT_EB_MUTATUION"],
  });
  const getClientLoading = !!useIsMutating({
    mutationKey: ["GET_CLIENT_BY_ID"],
  });
  const complianceLoading = !!useIsMutating({
    mutationKey: ["GET_KYC_CLIENT_BY_ID"],
  });
  const clientIdLoading = !!useIsMutating({
    mutationKey: ["GET_CLIENT_BY_ID"],
  });
  const clientSendIdLoading = !!useIsMutating({
    mutationKey: ["HANDLE_CLIENT_SEND_LINK_MUTATUION"],
  });
  const paymentLinkLoading = !!useIsMutating({
    mutationKey: ["HANDLE_SEND_PAYMENT_LINK"],
  });
  const aumBasedLoading = !!useIsMutating({
    mutationKey: ["ADD_AUM_BASED_SUBSCRIPTION"],
  });
  const getKraLoading = !!useIsMutating({
    mutationKey: ["GET_KRA_DATA"],
  });
  const handleKYCLoading = !!useIsMutating({
    mutationKey: ["HANDLE_KYC_ADVISOR"],
  });
  const handleSendAgreementLoading = !!useIsMutating({
    mutationKey: ["CLIENT_ONBOARDING_SEND_CUSTOM_AGREEMENT"],
  });
  const handleAgreementLoading = !!useIsMutating({
    mutationKey: ["CLIENT_ONBOARDING_GENERATE_AGREEMENT"],
  });
  const handleHoldingParseLoading = !!useIsMutating({
    mutationKey: ["HANDLE_HOLDINGS_PARSING"],
  });

  const loading =
    riskLoading ||
    getKraLoading ||
    aumBasedLoading ||
    clientIdLoading ||
    agreementLoading ||
    handleKYCLoading ||
    addClientLoading ||
    getClientLoading ||
    paymentLinkLoading ||
    riskProfileLoading ||
    clientSendIdLoading ||
    updateClientLoading ||
    equityBasketLoading ||
    handleAgreementLoading ||
    handleHoldingParseLoading ||
    handleSendAgreementLoading ||
    complianceLoading;
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const activestepWatch = form.watch("activeStep");

  const filteredTabs = TabsData.filter((tab) => tab?.show);
  const activeTabValue = filteredTabs[activestepWatch]?.value;

  const handleReturnStepColor = ({
    index,
    dataItem,
  }: {
    index: number;
    dataItem: any;
  }) => {
    if (index === activestepWatch) {
      return "var(--primary-900)";
    }
    if (dataItem?.isCompleted) {
      return "var(--success-700)";
    }
    return "var(--neutral-500)";
  };

  console.log("form.getValues()", form.getValues());

  return (
    <FormProvider {...form}>
      <Modal open={open} onClose={() => setOpen(false)} disableEscapeKeyDown>
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            width: "100vw",
            justifyContent: "center",
            alignItems: {
              md: "center",
              xs: "flex-end",
            },
            borderRadius: 0,
          }}
        >
          <Grid2
            container
            sx={{
              width: {
                xs: "100%",
                md: "70%",
              },
            }}
            bgcolor={"#F2F2F2"}
            borderRadius={2}
            maxHeight={"90vh"}
            overflow={"hidden"}
          >
            <Show.When isTrue={form.getValues("showSendClientCompletion")}>
              <Grid2 size={12} bgcolor={"#fff"} borderRadius={2}>
                <SendClientModal setOpen={setOpen} />
              </Grid2>
            </Show.When>
            <Show.When isTrue={!form.getValues("showSendClientCompletion")}>
              <Grid2 size={12} container borderRadius={2}>
                <Show.When isTrue={form.getValues("activeStep") >= 1}>
                  <Grid2 size={3} p={4}>
                    <Tabs
                      orientation="vertical"
                      variant="scrollable"
                      value={form.getValues("activeStep")}
                      sx={{
                        "& .MuiTabs-list": {
                          gap: 0,
                        },
                        "& .MuiButtonBase-root": {
                          gap: 0,
                          justifyContent: "flex-start",
                        },
                      }}
                      onChange={(e, value) => {
                        if (form.getValues("id")) {
                          form.setValue("activeStep", value);
                        }
                      }}
                      TabIndicatorProps={{
                        style: {
                          backgroundColor: "var(--red-900)",
                        },
                      }}
                    >
                      {filteredTabs?.map((dataItem, index) => (
                        <Tab
                          sx={{
                            color: handleReturnStepColor({
                              dataItem: dataItem,
                              index: index,
                            }),
                            "&.Mui-disabled": {
                              color: handleReturnStepColor({
                                dataItem: dataItem,
                                index: index,
                              }),
                            },
                          }}
                          disabled={dataItem?.disabled}
                          key={dataItem.label}
                          label={dataItem.label}
                          icon={
                            dataItem?.isCompleted &&
                            index !== form.getValues("activeStep") ? (
                              <CheckFilledIcon
                                color={"var(--success-700)"}
                                height={20}
                                width={20}
                              />
                            ) : (
                              <dataItem.icon height={20} width={20} />
                            )
                          }
                          iconPosition="start"
                        />
                      ))}
                    </Tabs>
                  </Grid2>
                </Show.When>
                <Grid2
                  container
                  size={form.getValues("activeStep") === 0 ? 12 : 9}
                  bgcolor={"var(--white)"}
                  borderRadius={2}
                >
                  <Grid2
                    size={12}
                    px={2}
                    py={1}
                    justifyContent={"flex-end"}
                    display={"flex"}
                  >
                    <IconButton onClick={() => setOpen(false)}>
                      <CloseIcon height={20} width={20} />
                    </IconButton>
                  </Grid2>
                  <Grid2
                    size={12}
                    px={2}
                    py={1}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography
                      fontSize={20}
                      fontWeight={700}
                      color="var(--black-900)"
                    >
                      Onboarding
                    </Typography>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-500)"
                    >
                      Lorem ipsum dolor sit amet consectetur. Congue adipiscing
                      odio dui eu viverra pharetra porttitor tincidunt.
                    </Typography>
                  </Grid2>
                  <Grid2
                    size={12}
                    maxHeight={"calc(90vh - 210px)"}
                    overflow={"auto"}
                    px={2}
                    py={4}
                  >
                    {filteredTabs.map((d, i) => {
                      return (
                        <Show.When isTrue={activestepWatch === i}>
                          {d.component}
                        </Show.When>
                      );
                    })}
                  </Grid2>
                  <Grid2 size={12} height={"auto"}>
                    <Show.When isTrue={!form.getValues("uploadHoldingsModal")}>
                      <Grid2
                        size={12}
                        display={"flex"}
                        justifyContent={"space-between"}
                        gap={1}
                        px={2}
                        py={1}
                      >
                        <GKButton
                          startIcon={<ArrowLeftIcon />}
                          sx={{
                            visibility: [
                              "BASKET",
                              "INVESTMENTS",
                              "KYC",
                              "RISK_ASSESSMENT",
                              "AGREEMENT",
                              "PAYMENT",
                              "COMPLETED",
                            ].includes(activeTabValue)
                              ? "visible"
                              : "hidden",
                          }}
                          onClick={() => {
                            form.setValue(
                              "activeStep",
                              form.getValues("activeStep") - 1,
                            );
                          }}
                        >
                          Back
                        </GKButton>
                        <Show.When
                          isTrue={
                            activeTabValue === "RISK_ASSESSMENT" &&
                            riskProfileMatch
                          }
                        >
                          <GKButton
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              form.setValue("riskProfile", null);
                              form.setValue("answers", []);
                            }}
                            sx={{ ml: "auto" }}
                          >
                            Retake Assessment
                          </GKButton>
                        </Show.When>
                        <Show.When
                          isTrue={
                            activeTabValue === "RISK_ASSESSMENT" &&
                            !form.getValues("riskProfile.points")
                          }
                        >
                          <GKButton
                            variant="outlined"
                            color="dark"
                            onClick={() => {
                              sendRiskProfile(form.getValues("id"));
                            }}
                            sx={{ ml: "auto" }}
                            startIcon={<SendIcon />}
                            loading={loading}
                          >
                            Send to Client
                          </GKButton>
                        </Show.When>
                        <Show.When
                          isTrue={
                            activeTabValue === "PAYMENT" &&
                            !form.getValues("skipAdvanceFee") &&
                            onBoardflowData?.paymentMethods !== "manual_payment"
                          }
                        >
                          <GKButton
                            loading={sendLinkLoading || loading}
                            variant="outlined"
                            color="dark"
                            startIcon={<SendIcon />}
                            onClick={() => {
                              handleSendPaymentLink({
                                isSendEmail: true,
                                client: form.getValues("id"),
                                basket: form.getValues("basketObject.id"),
                                feePlan: form.getValues("selectedFeePlan.id"),
                                portfolio: form.getValues("portfolioObject.id"),
                                frequency: form.getValues(
                                  "advanceFeePlan.frequency",
                                ),
                                advanceFeePlan: form.getValues(
                                  "advanceFeePlan.feePlan.id",
                                ),
                                flowType: "Advisor Initiated Advisory Flow",
                              });
                            }}
                            sx={{ ml: "auto" }}
                          >
                            Send payment link
                          </GKButton>
                        </Show.When>
                        <Show.When
                          isTrue={
                            activeTabValue === "COMPLETED" && !isMFKYCComplete
                          }
                        >
                          <GKButton
                            loading={sendLinkLoading || loading}
                            variant="outlined"
                            color="dark"
                            startIcon={<AddUser1Icon />}
                            onClick={() => {
                              form.reset();
                            }}
                            sx={{ ml: "auto" }}
                          >
                            Add New Client
                          </GKButton>
                        </Show.When>
                        <GKButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          loading={loading}
                          disabled={
                            (activeTabValue === "RISK_ASSESSMENT" &&
                              !riskProfileMatch) ||
                            hasError
                          }
                          form={handleCurrentForm(activeTabValue).form}
                          endIcon={<ArrowRightIcon />}
                          onClick={() => {
                            if (activeTabValue === "COMPLETED") setOpen(false);
                          }}
                        >
                          {handleCurrentForm(activeTabValue).buttonText}
                        </GKButton>
                      </Grid2>
                    </Show.When>
                    <Show.When isTrue={form.getValues("uploadHoldingsModal")}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <GKButton
                          onClick={() => {
                            form.setValue("uploadHoldingsModal", false);
                            form.setValue("planDataObject.plansHoldings", []);
                          }}
                          variant="outlined"
                          color="dark"
                        >
                          Cancel
                        </GKButton>
                        <Typography>
                          Total Investment value:{" "}
                          <span
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            {numberToCurrency(
                              +sum(
                                form
                                  .getValues("planDataObject.plansHoldings")
                                  .map((d) => +d.cmp * +d.quantity),
                              ) || 0,
                            )}
                          </span>
                        </Typography>
                        <GKButton
                          onClick={() => {
                            form.setValue(
                              "planDataObject.holdingsValue",
                              +sum(
                                form
                                  .getValues("planDataObject.plansHoldings")
                                  .map((d) => +d.cmp * +d.quantity),
                              ),
                            );
                            form.setValue("uploadHoldingsModal", false);
                          }}
                          variant="contained"
                        >
                          Save Holdings
                        </GKButton>
                      </Box>
                    </Show.When>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Show.When>
          </Grid2>
        </Box>
      </Modal>
    </FormProvider>
  );
};

export default AdvisorOnboarding;
